import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Users, Package, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import VendorModule from "@/components/VendorModule";
import BudgetModule from "@/components/BudgetModule";
import TimelineModule from "@/components/TimelineModule";
import GuestModule from "@/components/modules/GuestModule";
import TaskModule from "@/components/modules/TaskModule";
import EmergencyModule from "@/components/modules/EmergencyModule";
import MenuModule from "@/components/modules/MenuModule";
import EInviteModule from "@/components/modules/EInviteModule";
import EnhancedAppHeader from "@/components/EnhancedAppHeader";
import { TrialBanner } from "@/components/TrialBanner";
import { useCurrency } from "@/hooks/useCurrency";
import { weddingService } from "@/services/wedding.service";
import { budgetService } from "@/services/budget.service";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function WeddingDetail() {
  const { id } = useParams();
  const { userRole, activeOrg } = useAuth();
  const isClient = userRole === 'client' || userRole === 'read_only_client';
  const { formatCurrency, formatDate } = useCurrency();
  const navigate = useNavigate();
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendorStats, setVendorStats] = useState({ total: 0, nonHotel: 0, totalPaid: 0 });
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [vendorRefreshTrigger, setVendorRefreshTrigger] = useState(0);

  useEffect(() => {
    loadWedding();
    // Only load budget categories for non-clients
    if (!isClient && activeOrg?.id) {
      loadBudgetCategories();
    }
  }, [id, isClient, activeOrg]);

  const loadWedding = async () => {
    try {
      setLoading(true);
      const data = await weddingService.getWedding(id);
      setWedding(data);
    } catch (error) {
      console.error('Failed to load wedding:', error);
      toast.error('Failed to load wedding');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeSuccess = () => {
    // Reload wedding to get updated trial status
    loadWedding();
  };

  const loadBudgetCategories = async () => {
    if (!activeOrg?.id) return;
    try {
      const categories = await budgetService.getBudgetCategories(id, activeOrg.id);
      setBudgetCategories(categories);
    } catch (error) {
      console.error('Failed to load budget categories:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading wedding details...</p>
        </div>
      </div>
    );
  }

  if (!wedding) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-soft">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Wedding not found</h2>
          <Button onClick={() => navigate("/")}>
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const displayVendorCount = vendorStats.nonHotel || wedding.vendor_count || 0;
  
  // Calculate total spent including vendor payments and miscellaneous expenses
  const miscExpensesTotal = budgetCategories
    .filter(cat => cat.name === 'Miscellaneous')
    .reduce((sum, cat) => sum + (cat.spent || 0), 0);
  const displaySpent = (vendorStats.totalPaid || wedding.spent || 0) + miscExpensesTotal;

  return (
    <div className="min-h-screen gradient-soft pb-12">
      {/* Enhanced Header with Profile Menu */}
      <div className="sticky top-0 z-50">
        <EnhancedAppHeader
          title={isClient ? "Your Wedding" : `${wedding.bride_name} & ${wedding.groom_name}`}
          subtitle={
            <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(wedding.wedding_date)}
              </span>
              {wedding.venue && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {wedding.venue}
                </span>
              )}
              <Badge className="gradient-primary text-primary-foreground px-2 py-0.5 text-xs">
                {Math.ceil((new Date(wedding.wedding_date) - new Date()) / (1000 * 60 * 60 * 24))} days to go
              </Badge>
            </div>
          }
        />
        
        {/* Back Button Below Header - Hide for clients */}
        {!isClient && (
          <div className="bg-card/80 backdrop-blur-lg border-b border-border">
            <div className="container mx-auto px-4 py-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/")}
                className="hover:bg-primary/10"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Weddings
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {!isClient && (
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="gradient-primary rounded-lg p-4 text-primary-foreground shadow-medium">
              <IndianRupee className="h-5 w-5 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{formatCurrency(wedding.budget)}</p>
              <p className="text-xs opacity-90">Total Budget</p>
            </div>
            
            <div className="gradient-accent rounded-lg p-4 text-accent-foreground shadow-medium">
              <Users className="h-5 w-5 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{wedding.guest_count || 0}</p>
              <p className="text-xs opacity-90">Guests</p>
            </div>
            
            <div className="gradient-gold rounded-lg p-4 text-foreground shadow-medium">
              <Package className="h-5 w-5 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{displayVendorCount}</p>
              <p className="text-xs opacity-90">Vendors</p>
            </div>
            
            <div className="bg-success rounded-lg p-4 text-primary-foreground shadow-medium">
              <IndianRupee className="h-5 w-5 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{formatCurrency(displaySpent)}</p>
              <p className="text-xs opacity-90">Spent</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Client Stats - Only Guest Count */}
      {isClient && (
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-md mx-auto">
            <div className="gradient-accent rounded-lg p-4 text-accent-foreground shadow-medium">
              <Users className="h-5 w-5 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{wedding.guest_count || 0}</p>
              <p className="text-xs opacity-90">Guests</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="container mx-auto px-4">
        <Tabs defaultValue={isClient ? "guests" : "vendors"} className="w-full">
          <TabsList className={`w-full flex flex-nowrap md:grid gap-1 md:gap-2 h-auto p-1 bg-card border border-border overflow-x-auto md:overflow-visible ${isClient ? 'md:grid-cols-2' : 'md:grid-cols-7'}`}>
            {!isClient && (
              <>
                <TabsTrigger value="vendors" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-[10px] sm:text-xs md:text-sm px-1.5 sm:px-2 md:px-4 whitespace-nowrap min-w-0 flex-shrink-0">
                  <span className="hidden lg:inline">Vendors & Hotels</span>
                  <span className="hidden md:inline lg:hidden">Vendors</span>
                  <span className="md:hidden">Vendors</span>
                </TabsTrigger>
                <TabsTrigger value="budget" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-[10px] sm:text-sm px-2 md:px-4">Budget</TabsTrigger>
                <TabsTrigger value="timeline" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-[10px] sm:text-sm px-2 md:px-4">Timeline</TabsTrigger>
                <TabsTrigger value="tasks" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-[10px] sm:text-sm px-2 md:px-4">Tasks</TabsTrigger>
                <TabsTrigger value="emergency" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-[10px] sm:text-sm px-2 md:px-4">
                  <span className="hidden sm:inline">Emergency</span>
                  <span className="sm:hidden">SOS</span>
                </TabsTrigger>
              </>
            )}
            <TabsTrigger value="menu" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-[10px] sm:text-sm px-2 md:px-4">Menu</TabsTrigger>
            <TabsTrigger value="guests" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-[10px] sm:text-sm px-2 md:px-4">Guests</TabsTrigger>
            {!isClient && (
              <TabsTrigger value="einvite" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-[10px] sm:text-sm px-2 md:px-4">
                <span className="hidden sm:inline">E-Invite</span>
                <span className="sm:hidden">Invite</span>
              </TabsTrigger>
            )}
          </TabsList>

          {!isClient && (
            <>
              <TabsContent value="vendors" className="mt-6">
                <VendorModule weddingId={wedding.id} wedding={wedding} onStatsChange={setVendorStats} onWeddingUpdated={loadWedding} refreshTrigger={vendorRefreshTrigger} />
              </TabsContent>

              <TabsContent value="budget" className="mt-6">
                <BudgetModule weddingId={wedding.id} wedding={wedding} onBudgetUpdate={loadBudgetCategories} />
              </TabsContent>

              <TabsContent value="timeline" className="mt-6">
                <TimelineModule weddingId={wedding.id} wedding={wedding} onEventDeleted={() => setVendorRefreshTrigger(prev => prev + 1)} />
              </TabsContent>

              <TabsContent value="tasks" className="mt-6">
                <TaskModule weddingId={wedding.id} wedding={wedding} />
              </TabsContent>

              <TabsContent value="emergency" className="mt-6">
                <EmergencyModule weddingId={wedding.id} />
              </TabsContent>
            </>
          )}

          <TabsContent value="menu" className="mt-6">
            <MenuModule weddingId={wedding.id} wedding={wedding} />
          </TabsContent>

          <TabsContent value="guests" className="mt-6">
            <GuestModule weddingId={wedding.id} wedding={wedding} />
          </TabsContent>

          {!isClient && (
            <TabsContent value="einvite" className="mt-6">
              <EInviteModule weddingId={wedding.id} wedding={wedding} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
